import Column from 'Components/Base/Column'
import Icon from 'Components/Base/Icon'
import Row from 'Components/Base/Row'
import Typography from 'Components/Base/Typography'
import dayjs from 'dayjs'
import { useAppSelector } from 'hooks/hooks'
import useAppointments from 'hooks/useAppointments'
import useNewEnrollment from 'hooks/useNewEnrollment'
import React, { useEffect, useState } from 'react'
import { getAccessToken } from 'reducers/authSlice'
import { useTheme } from 'styled-components'
import ScheduleCard from './ScheduleCard'

interface Card {
  cardId: number
  title: string
  subText?: React.ReactChild
  buttonText: string
  buttonRoute?: string
  img: string
  dynamic?: boolean
  changeApptStep?: number
  isInitial?: boolean
}

interface props {
  handleSetStep: (step: number, isInitial: boolean, cardId: number) => void
  handleSetCtl: (ctl: boolean) => void
}

export default function ScheduleCards({ handleSetStep, handleSetCtl }: props) {
  const theme = useTheme()
  const token = useAppSelector(getAccessToken)
  const { getScheduleCards } = useNewEnrollment({ token: token })
  const { getAppointments, getCTLAppointments } = useAppointments(token!)
  const [paCard, setPaCard] = useState<Card>()
  const [ctlCard, setCtlCard] = useState<Card>()
  const [triaPharm, setTriaPharm] = useState<string>('Tria')
  const [datePA, setDatePA] = useState<string>(dayjs().format('MM/DD/YYYY'))
  const [timePA, setTimePA] = useState<string>(dayjs().format('h:mma'))
  const [triaHC, setTriaHC] = useState<string>('Tria')
  const [dateCTL, setDateCTL] = useState<string>(dayjs().format('MM/DD/YYYY'))
  const [timeCTL, setTimeCTL] = useState<string>(dayjs().format('h:mma'))

  const cards: readonly Card[] = [
    {
      cardId: 1,
      title: 'Schedule a follow-up with your pharmacist',
      subText: (
        <div>
          <Typography>
            Your Tria Health pharmacist is here to help you stay on track. Let us know how you’re doing and schedule your follow-up today!
          </Typography>
        </div>
      ),
      buttonText: 'Schedule Follow-up',
      changeApptStep: 2,
      isInitial: false,
      img: 'https://triacentral.blob.core.windows.net/dev-storage/portal/ScheduleCards/Woman%20on%20headset%20-%20blue%201.png',
    },
    {
      cardId: 2,
      title: 'Schedule a follow-up with your health coach',
      subText: (
        <div>
          <Typography>
            Your Tria Health coach is here to help you stay on track. You can let us know how you’re doing by{' '}
            <a href="/messageCenter">sending us a message</a> or by scheduling your follow-up!
          </Typography>
        </div>
      ),
      buttonText: 'Schedule Follow-up',
      changeApptStep: 2,
      isInitial: false,
      img: 'https://triacentral.blob.core.windows.net/dev-storage/portal/ScheduleCards/Health%20Coach%201.png',
    },
    {
      cardId: 3,
      title: 'You’re Scheduled!',
      dynamic: true,
      buttonText: 'Reschedule',
      changeApptStep: 2,
      isInitial: false,
      img: 'https://triacentral.blob.core.windows.net/dev-storage/portal/ScheduleCards/Pill%20and%20Water%201.png',
    },
    {
      cardId: 4,
      title: 'You’re Scheduled!',
      dynamic: true,
      buttonText: 'Reschedule',
      changeApptStep: 2,
      isInitial: false,
      img: 'https://triacentral.blob.core.windows.net/dev-storage/portal/ScheduleCards/Nutrition%20Summary%201.png',
    },
    {
      cardId: 5,
      title: "You're All Set!",
      subText: (
        <Column>
          <Typography>
            You recently met with a health coach, so there's no need to book another appointment right now.
            <b> We'll let you know when it's time for your next check-in.</b>
          </Typography>
          <Typography>
            If you have a quick question or want to talk, you can call us at 913.322.8456 or send a message through the Message Center.
          </Typography>
        </Column>
      ),
      buttonText: 'Send a Message',
      buttonRoute: '/messaging',
      img: 'https://triacentral.blob.core.windows.net/dev-storage/portal/ScheduleCards/Health%20Coach%201.png',
    },
    {
      cardId: 6,
      title: "You're All Set!",
      subText: (
        <Column>
          <Typography>
            You recently met with a pharmacist, so there's no need to book another appointment right now.
            <b> We'll let you know when it's time for your next check-in.</b>
          </Typography>
          <Typography>
            If you have a quick question or want to talk, you can call us at 913.322.8456 or send a message through the Message Center.
          </Typography>
        </Column>
      ),
      buttonText: 'Send a Message',
      buttonRoute: '/messaging',
      img: 'https://triacentral.blob.core.windows.net/dev-storage/portal/ScheduleCards/Close%20up%20of%20assorted%20pills_Extended%201.png',
    },
    {
      cardId: 7,
      title: 'Make sure your medications are working',
      subText: (
        <Column>
          <Row styles={{ marginTop: 15 }}>
            <Icon name="circleCheck" color={theme.colors.primary1} styles={{ marginTop: 'auto', marginBottom: 'auto', marginRight: 10 }} />
            <Typography size={16} styles={{ lineHeight: '117%' }}>
              Review your prescriptions, over-the-counter meds, and supplements
            </Typography>
          </Row>
          <Row styles={{ marginTop: 15 }}>
            <Icon name="circleCheck" color={theme.colors.primary1} styles={{ marginTop: 'auto', marginBottom: 'auto', marginRight: 10 }} />
            <Typography size={16} styles={{ lineHeight: '117%' }}>
              Talk about your lifestyle and health goals
            </Typography>
          </Row>
          <Row styles={{ marginTop: 15 }}>
            <Icon name="circleCheck" color={theme.colors.primary1} styles={{ marginTop: 'auto', marginBottom: 'auto', marginRight: 10 }} />
            <Typography size={16} styles={{ lineHeight: '117%' }}>
              Discover ways to save money on prescriptions
            </Typography>
          </Row>
          <Row styles={{ marginTop: 15 }}>
            <Icon name="circleCheck" color={theme.colors.primary1} styles={{ marginTop: 'auto', marginBottom: 'auto', marginRight: 10 }} />
            <Typography size={16} styles={{ lineHeight: '117%' }}>
              Make sure you're getting the care and support you need
            </Typography>
          </Row>
        </Column>
      ),
      buttonText: 'Get Started',
      changeApptStep: 2,
      isInitial: true,
      img: 'https://triacentral.blob.core.windows.net/dev-storage/portal/ScheduleCards/Woman%20on%20headset%20-%20blue%201.png',
    },
    {
      cardId: 8,
      title: 'Struggling to lose weight alone? Let us help.',
      subText: (
        <Column>
          <Row styles={{ marginTop: 15 }}>
            <Icon name="circleCheck" color={theme.colors.primary1} styles={{ marginTop: 'auto', marginBottom: 'auto', marginRight: 10 }} />
            <Typography size={16} styles={{ lineHeight: '117%' }}>
              Work with a personal health coach
            </Typography>
          </Row>
          <Row styles={{ marginTop: 15 }}>
            <Icon name="circleCheck" color={theme.colors.primary1} styles={{ marginTop: 'auto', marginBottom: 'auto', marginRight: 10 }} />
            <Typography size={16} styles={{ lineHeight: '117%' }}>
              Get a plan that fits your life and goals
            </Typography>
          </Row>
          <Row styles={{ marginTop: 15 }}>
            <Icon name="circleCheck" color={theme.colors.primary1} styles={{ marginTop: 'auto', marginBottom: 'auto', marginRight: 10 }} />
            <Typography size={16} styles={{ lineHeight: '117%' }}>
              Receive a welcome kit with a cellular scale, water bottle, tape measure, and portion plate
            </Typography>
          </Row>
          <Row styles={{ marginTop: 15 }}>
            <Icon name="circleCheck" color={theme.colors.primary1} styles={{ marginTop: 'auto', marginBottom: 'auto', marginRight: 10 }} />
            <Typography size={16} styles={{ lineHeight: '117%' }}>
              Track your meals and progress in the Tria Health app
            </Typography>
          </Row>
        </Column>
      ),
      buttonText: 'Get Started',
      changeApptStep: 2,
      isInitial: true,
      img: 'https://triacentral.blob.core.windows.net/dev-storage/portal/ScheduleCards/Choose%20to%20Lose%20%20Kit%201.png',
    },
    {
      cardId: 9,
      title: 'You’re Scheduled!',
      dynamic: true,
      buttonText: 'Reschedule',
      changeApptStep: 2,
      isInitial: true,
      img: 'https://triacentral.blob.core.windows.net/dev-storage/portal/ScheduleCards/Pill%20and%20Water%201.png',
    },
    {
      cardId: 10,
      title: 'You’re Scheduled!',
      dynamic: true,
      buttonText: 'Reschedule',
      changeApptStep: 2,
      isInitial: true,
      img: 'https://triacentral.blob.core.windows.net/dev-storage/portal/ScheduleCards/Nutrition%20Summary%201.png',
    },
  ] as const

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      let paCardTemp: any = undefined
      let ctlCardTemp: any = undefined

      const res = await getScheduleCards()
      const pending: Promise<void>[] = []

      if (res.data.showPA) {
        if (res.data.paCard) {
          paCardTemp = res.data.paCard
          setTriaPharm(res.data.pharmacistName)
          setDatePA(dayjs(res.data.paDate).format('M/D/YYYY'))
          setTimePA(dayjs(res.data.paDate).format('M/D/YYYY h:mm:ss A'))
        } else {
          pending.push(
            getAppointments().then((a) => {
              paCardTemp = a.data.canSchedule ? 1 : 6
            }),
          )
        }
      } else {
        setPaCard(undefined)
      }

      if (res.data.showCTL) {
        if (res.data.ctlCard) {
          ctlCardTemp = res.data.ctlCard
          setTriaHC(res.data.healthCoachName)
          setDateCTL(dayjs(res.data.ctlDate).format('M/D/YYYY'))
          setTimeCTL(dayjs(res.data.ctlDate).format('M/D/YYYY h:mm:ss A'))
        } else {
          pending.push(
            getCTLAppointments().then((a) => {
              ctlCardTemp = a.data.canSchedule ? 2 : 5
            }),
          )
        }
      } else {
        setCtlCard(undefined)
      }

      await Promise.all(pending)
      if (cancelled) return

      const nextPa = cards.find((card) => card.cardId === paCardTemp) ?? null
      const nextCtl = cards.find((card) => card.cardId === ctlCardTemp) ?? null
      // Only update when IDs actually change
      nextPa && setPaCard((prev) => (prev?.cardId === nextPa?.cardId ? prev : nextPa))
      nextCtl && setCtlCard((prev) => (prev?.cardId === nextCtl?.cardId ? prev : nextCtl))
    })()

    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Column styles={{ padding: 10 }}>
      {paCard && (
        <ScheduleCard
          details={paCard}
          key={paCard.cardId}
          dynamic={paCard.dynamic ?? false}
          triaPerson={triaPharm}
          date={datePA}
          time={timePA}
          handleChangeApptStep={(step: number, isInitial: boolean, cardId: number) => {
            handleSetCtl(false)
            handleSetStep(step, isInitial, cardId)
          }}
        />
      )}
      {ctlCard && (
        <ScheduleCard
          details={ctlCard}
          key={ctlCard.cardId}
          dynamic={ctlCard.dynamic ?? false}
          triaPerson={triaHC}
          date={dateCTL}
          time={timeCTL}
          handleChangeApptStep={(step: number, isInitial: boolean, cardId: number) => {
            handleSetCtl(true)
            handleSetStep(step, isInitial, cardId)
          }}
        />
      )}
    </Column>
  )
}
